import {
  forwardRef,
  useId,
  useMemo,
  type ComponentType,
  type KeyboardEvent,
  type MouseEvent,
  type TouchEvent,
} from "react";
import { type ReferenceType } from "@floating-ui/react";
import { twMerge } from "tailwind-merge";
import { IconButton } from "@components/IconButton/IconButton";
import { ExclamationCircleIcon, LoaderIcon } from "@components/icons";
import { Label } from "@components/Label/Label";
import { Tooltip } from "@components/Tooltip/Tooltip";
import { isNullOrUndefined } from "@utils/common";

export interface IInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * Input label text.
   */
  label?: string;
  /**
   * Hint text. Will be hidden if error is set.
   */
  hint?: string;
  /**
   * Input size.
   */
  inputSize?: "small" | "medium" | "large";
  /**
   * Error text.
   */
  error?: string;
  /**
   * Left icon element.
   */
  iconLeft?: ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Right icon element.
   */
  iconRight?: ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Right icon click callback.
   */
  onRightIconClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Right icon key down callback.
   */
  onRightIconKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  /**
   * Right icon key up callback.
   */
  onRightIconKeyUp?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  /**
   * Right icon mouse down callback.
   */
  onRightIconMouseDown?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Right icon mouse up callback.
   */
  onRightIconMouseUp?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Right icon touch start callback.
   */
  onRightIconTouchStart?: (e: TouchEvent<HTMLButtonElement>) => void;
  /**
   * Right icon touch end callback.
   */
  onRightIconTouchEnd?: (e: TouchEvent<HTMLButtonElement>) => void;
  /**
   * Right icon tooltip content;
   */
  iconRightTooltipContent?: string;
  /**
   * Input loading state flag.
   */
  isLoading?: boolean;
  /**
   * Icon classname
   */
  iconClassName?: string;
  /**
   * Container classname
   */
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, IInputProps>((props, ref) => {
  const {
    label,
    className,
    containerClassName,
    iconClassName,
    hint,
    inputSize = "large",
    error,
    iconLeft: IconLeft,
    iconRight: IconRight,
    onRightIconClick,
    onRightIconKeyDown,
    onRightIconKeyUp,
    onRightIconMouseDown,
    onRightIconMouseUp,
    onRightIconTouchStart,
    onRightIconTouchEnd,
    iconRightTooltipContent,
    disabled,
    isLoading,
    id,
    ...rest
  } = props;
  const inputId = useId();

  const generateRightIconElementBase = useMemo(
    () => (props: { ref?: (node: ReferenceType | null) => void }) => {
      const isClickable =
        !isNullOrUndefined(onRightIconClick) ||
        !isNullOrUndefined(onRightIconKeyDown) ||
        !isNullOrUndefined(onRightIconMouseDown);
      const className = twMerge(
        "absolute inset-y-0 right-3.5 z-0 m-auto h-5 w-5 text-grey-dark",
        (isClickable ?? !isNullOrUndefined(iconRightTooltipContent)) && "z-10",
        isClickable && "cursor-pointer",
        iconClassName,
      );
      if (!isNullOrUndefined(IconRight) && !isNullOrUndefined(isClickable)) {
        return (
          <IconButton
            icon={IconRight}
            onClick={onRightIconClick}
            onKeyDown={onRightIconKeyDown}
            onKeyUp={onRightIconKeyUp}
            onMouseDown={onRightIconMouseDown}
            onMouseUp={onRightIconMouseUp}
            onTouchStart={onRightIconTouchStart}
            onTouchEnd={onRightIconTouchEnd}
            className={className}
            iconClassName={twMerge("h-5 w-5", iconClassName)}
            type="button"
            {...props}
          />
        );
      } else if (
        !isNullOrUndefined(IconRight) &&
        !isNullOrUndefined(iconRightTooltipContent)
      ) {
        return (
          <div className={className} {...props}>
            <IconRight className="h-5 w-5" />
          </div>
        );
      } else if (!isNullOrUndefined(IconRight)) {
        return <IconRight className={className} {...props} />;
      } else {
        return null;
      }
    },
    [
      IconRight,
      iconRightTooltipContent,
      onRightIconClick,
      iconClassName,
      onRightIconKeyDown,
      onRightIconKeyUp,
      onRightIconMouseDown,
      onRightIconMouseUp,
      onRightIconTouchStart,
      onRightIconTouchEnd,
    ],
  );

  const rightIcon = useMemo(() => {
    if (!isNullOrUndefined(iconRightTooltipContent)) {
      return (
        <Tooltip content={iconRightTooltipContent}>
          {generateRightIconElementBase}
        </Tooltip>
      );
    } else {
      return generateRightIconElementBase({});
    }
  }, [generateRightIconElementBase, iconRightTooltipContent]);

  return (
    <div
      className={twMerge("flex w-full flex-col gap-1.5", containerClassName)}
    >
      {label && <Label htmlFor={id ?? inputId}>{label}</Label>}
      <div
        className={twMerge(
          "relative h-11 w-full overflow-hidden rounded-full border border-grey-hover bg-base-white transition-shadow focus-within:border-green-dark focus-within:ring-4 focus-within:ring-green-dark focus-within:ring-opacity-10",
          inputSize === "small" && "h-9",
          inputSize === "medium" && "h-10",
          error &&
            "border-red-dark focus-within:border-red-dark focus-within:ring-red-dark",
          (disabled ?? isLoading) && "bg-base-light-grey text-grey-dark",
        )}
      >
        <input
          id={id ?? inputId}
          className={twMerge(
            "relative z-10 h-full w-full border-0 bg-transparent px-3.5 py-2.5 text-base-black outline-none placeholder:text-grey-dark disabled:text-grey-dark",
            inputSize === "small" && "py-1.5",
            inputSize === "medium" && "py-2",
            IconLeft && "pl-10",
            (IconRight ?? error ?? isLoading) && "pr-10",
            className,
          )}
          disabled={disabled ?? isLoading}
          ref={ref}
          {...rest}
          autoComplete="off"
        />
        {IconLeft && (
          <IconLeft
            className={twMerge(
              "absolute inset-y-0 left-3.5 z-0 m-auto h-5 w-5 text-grey-dark",
              iconClassName,
            )}
          />
        )}
        {isLoading && (
          <LoaderIcon
            className={twMerge(
              "absolute inset-y-0 right-3.5 z-0 m-auto h-5 w-5 animate-spin text-grey-dark",
              iconClassName,
            )}
          />
        )}
        {!error && !isLoading && rightIcon}
        {error && !isLoading && (
          <ExclamationCircleIcon
            className={twMerge(
              "absolute inset-y-0 right-3.5 z-0 m-auto h-5 w-5 text-red-dark",
              iconClassName,
            )}
          />
        )}
      </div>
      {hint && !error && <span className="text-sm text-grey-dark">{hint}</span>}
      {error && (
        <span className="break-all text-sm text-red-dark">{error}</span>
      )}
    </div>
  );
});
